'use client';

import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { carePatientsKeys } from '@/entities/care-patients';
import { useCareStateDomain } from '@/entities/care-state-domain';
import { careTimelineKeys } from '@/entities/care-timeline';
import { karmaKeys } from '@/entities/karma';
import { userChatKeys } from '@/entities/user-chat';

/**
 * CareStateQuerySync
 *
 * Bridge feature: when CareState polling/updates observe any change and the engine is settled
 * (no pending updates), invalidate a set of React Query caches that are derived from CareState.
 */
export function CareStateQuerySync() {
  const queryClient = useQueryClient();
  const { careState, patientUserId, debug } = useCareStateDomain();

  const isInitializedRef = useRef(false);
  const needsRefreshRef = useRef(false);
  const previousCursorRef = useRef<number | null>(null);
  const previousPendingUpdateCountRef = useRef<number>(0);

  useEffect(() => {
    if (careState === null) {
      return;
    }

    if (patientUserId.trim() === '') {
      return;
    }

    if (!isInitializedRef.current) {
      isInitializedRef.current = true;
      previousCursorRef.current = debug.serverCursor;
      previousPendingUpdateCountRef.current = debug.pendingUpdateCount;
      return;
    }

    const hasEngineChange =
      previousCursorRef.current !== debug.serverCursor ||
      previousPendingUpdateCountRef.current !== debug.pendingUpdateCount;

    previousCursorRef.current = debug.serverCursor;
    previousPendingUpdateCountRef.current = debug.pendingUpdateCount;

    if (hasEngineChange) {
      needsRefreshRef.current = true;
    }

    if (!needsRefreshRef.current) {
      return;
    }

    // Avoid refetching too early: dependent endpoints may lag until local updates settle.
    if (debug.pendingUpdateCount > 0) {
      return;
    }

    needsRefreshRef.current = false;
    void queryClient.invalidateQueries({ queryKey: carePatientsKeys.all });
    void queryClient.invalidateQueries({ queryKey: careTimelineKeys.byUser(patientUserId) });
    void queryClient.invalidateQueries({ queryKey: userChatKeys.primaryByUser(patientUserId) });
    void queryClient.invalidateQueries({ queryKey: karmaKeys.all });
  }, [careState, debug.pendingUpdateCount, debug.serverCursor, patientUserId, queryClient]);

  return null;
}
